import { Button, PageHeader, Modal, Space, Spin, message } from 'antd'
import React, { useState, useEffect } from 'react'
import TableBlock from '../../components/tables/TableBlock'
import { getFixSku, getQrCodeByFixSku } from '../../network/FixSku'
import { ROLE_PERMISSIONS } from '../../constants/common'
import { useAuth } from '../../hooks/useAuth'
import { getCurrencySymbol } from '../../utils/currencyUtils'
import { createUrlWithFiltersFromKeyRequest } from '../../utils/filterUtils'
import useSearchParams from '../../hooks/useQuery'
import { Image } from 'antd'

const FixSkuList = () => {
  const params = useSearchParams()

  const { user } = useAuth()
  const permissions = ROLE_PERMISSIONS[user.role]
    ? ROLE_PERMISSIONS[user.role]
    : []

  const [currency, setCurrency] = useState('')
  const [qrModalFixSkuId, setQrModalFixSkuId] = useState(null)
  const [qrImageUrl, setQrImageUrl] = useState(null)
  const [qrLoading, setQrLoading] = useState(false)
  const [sku, setSku] = useState(null)
  const [qrError, setQrError] = useState(null)
  const baseUrl = process.env.REACT_APP_API_URL || ''
  const qrStorageFolder = '/storage/qr-codes'

  useEffect(() => {
    if (!qrModalFixSkuId) {
      setQrImageUrl(null)
      setQrError(null)
      return
    }
    setQrLoading(true)
    setQrError(null)
    getQrCodeByFixSku(qrModalFixSkuId)
      .then((data) => {
        const uri = data?.image?.uri
        setQrImageUrl(uri ? `${baseUrl}${qrStorageFolder}/${uri}` : null)
        setQrError(uri ? null : 'Invalid QR code response')
        setSku(data?.sku)
      })
      .catch(() => {
        setQrError('Failed to load QR code')
        message.error('Failed to load QR code')
      })
      .finally(() => setQrLoading(false))
  }, [qrModalFixSkuId, baseUrl])

  const closeQrModal = () => {
    setQrModalFixSkuId(null)
  }

  const printQrCode = () => {
    if (!qrImageUrl) return
    const win = window.open('', '_blank')
    const qrCount = 24
    const qrCells = Array(qrCount)
      .fill(null)
      .map(
        () =>
          `<div style="padding:2px;box-sizing:border-box;"><img src="${qrImageUrl}" alt="QR Code" style="width:100%;height:auto;max-width:120px;display:block;" /></div>`
      )
      .join('')
    win.document.write(`
      <html>
        <head>
          <title>QR Code ${sku || ''}</title>
          <style>
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body style="margin:0;padding:10px;box-sizing:border-box;">
          <div style="display:grid;grid-template-columns:repeat(4, 1fr);gap:0;width:100%;height:98vh;box-sizing:border-box;">
            ${qrCells}
          </div>
        </body>
      </html>
    `)
    win.document.close()
    win.onload = () => {
      win.print()
      win.onafterprint = () => win.close()
    }
  }

  const filters = (history) => ({
    fields: [
      {
        type: 'text',
        label: 'SKU',
        key: 'sku',
      },
      {
        type: 'text',
        label: 'Name (english)',
        key: 'name.en',
      },
      {
        type: 'text',
        label: 'Name (中文)',
        key: 'name.zh',
      },
      {
        type: 'select',
        label: 'Gender',
        default: '',
        key: 'gender',
        options: [
          { key: 'male', label: 'Male' },
          { key: 'female', label: 'Female' },
          { key: 'both', label: 'Both' },
        ],
      },
    ],
    actions: [
      {
        label: '+ Create fix SKU',
        isActive: permissions.includes('create-fixed-sku'),
        action: (keyRequest) => {
          let currentPage = null
          const filterValues = params.get('filterValues')
          if (filterValues) {
            try {
              const allFilters = JSON.parse(decodeURIComponent(filterValues))
              if (allFilters._page) {
                currentPage = parseInt(allFilters._page, 10)
              }
            } catch (e) {}
          }
          if (!currentPage) {
            const pageParam = params.get('page')
            if (pageParam) {
              currentPage = parseInt(pageParam, 10)
            }
          }

          const url = createUrlWithFiltersFromKeyRequest(
            `/fixed-sku/create/${user.currentStore}`,
            keyRequest,
            currentPage
          )
          history.push(url)
        },
      },
    ],
  })

  const columns = (history, keyRequest) => {
    const franchiseStore = user.franchiseStore
    return [
      {
        title: 'SKU',
        key: 'name',
        dataIndex: 'sku',
      },
      {
        title: 'Name (English)',
        key: 'name.en',
        dataIndex: 'name',
        render: (name) => name?.en,
        sorter: true,
      },
      ...(franchiseStore
        ? []
        : [
            {
              title: 'Name (中文)',
              key: 'name.zh',
              dataIndex: 'name',
              render: (name) => name?.zh,
            },
          ]),
      {
        title: `Retail price (${currency})`,
        key: 'name',
        sorter: true,
        render: (e) => {
          let symbol = ''
          if (user.franchise === false) {
            symbol = getCurrencySymbol(e.store.currency)
          }

          if (user.franchiseStore) {
            const currencyTotalDue = {
              cny: 'RMB',
              eur: 'EUR',
              usd: 'USD',
              gbp: 'GBP',
              jpy: 'JPY',
              aud: 'AUD',
              cad: 'CAD',
              chf: 'CHF',
              hkd: 'HKD',
              nzd: 'NZD',
            }
            setCurrency(currencyTotalDue[e.store.currency] || '')
          }

          return <div>{`${e.retailPrice} ${symbol}`}</div>
        },
      },
      {
        title: 'Delivery days',
        key: 'name',
        dataIndex: 'deliveryTime',
        sorter: true,
      },
      {
        title: '',
        key: 'action',
        width: 25,
        fixed: 'right',
        render: (e) => {
          return (
            <Space>
              <Button
                onClick={() => {
                  let currentPage = null
                  const filterValues = params.get('filterValues')
                  if (filterValues) {
                    try {
                      const allFilters = JSON.parse(
                        decodeURIComponent(filterValues)
                      )
                      if (allFilters._page) {
                        currentPage = parseInt(allFilters._page, 10)
                      }
                    } catch (err) {}
                  }
                  if (!currentPage) {
                    const pageParam = params.get('page')
                    if (pageParam) {
                      currentPage = parseInt(pageParam, 10)
                    }
                  }
                  const url = createUrlWithFiltersFromKeyRequest(
                    `fixed-sku/view/${e.id}/${user.currentStore}`,
                    keyRequest,
                    currentPage
                  )
                  history.push(url)
                }}
              >
                View
              </Button>
              <Button
                onClick={() => setQrModalFixSkuId(e.id)}
              >
                QR Code
              </Button>
              <Button
                onClick={() => {
                  let currentPage = null
                  const filterValues = params.get('filterValues')
                  if (filterValues) {
                    try {
                      const allFilters = JSON.parse(
                        decodeURIComponent(filterValues)
                      )
                      if (allFilters._page) {
                        currentPage = parseInt(allFilters._page, 10)
                      }
                    } catch (err) {}
                  }
                  if (!currentPage) {
                    const pageParam = params.get('page')
                    if (pageParam) {
                      currentPage = parseInt(pageParam, 10)
                    }
                  }

                  const url = createUrlWithFiltersFromKeyRequest(
                    `fixed-sku/${e.id}/${user.currentStore}`,
                    keyRequest,
                    currentPage
                  )
                  history.push(url)
                }}
                disabled={permissions.includes('edit-fixed-sku') ? false : true}
                type="primary"
              >
                Edit
              </Button>
            </Space>
          )
        },
      },
    ]
  }

  return (
    <>
      <PageHeader className="site-page-header" title="Fix SKU" />
      <TableBlock
        columns={columns}
        filters={filters}
        name={'getFixSku'}
        request={getFixSku}
        otherFilters={
          user.franchiseStore || !permissions.includes('can-see-franchises')
            ? { store: user.currentStore }
            : {}
        } // Filter the product if the store is franchised
      />
      <Modal
        title={sku ? `QR Code ${sku}` : 'QR Code'}
        visible={!!qrModalFixSkuId}
        onCancel={closeQrModal}
        footer={[
          <Button key="close" onClick={closeQrModal}>
            Close
          </Button>,
          <Button
            key="print"
            type="primary"
            onClick={printQrCode}
            disabled={qrLoading || !!qrError || !qrImageUrl}
          >
            Print
          </Button>,
        ]}
        width={400}
      >
        {qrLoading && (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin size="large" />
          </div>
        )}
        {qrError && (
          <div style={{ textAlign: 'center', padding: 24, color: '#ff4d4f' }}>
            {qrError}
          </div>
        )}
        {!qrLoading && !qrError && qrImageUrl && (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Image
              src={`${qrImageUrl}`}
              alt="QR Code"
              style={{ maxWidth: 375, maxHeight: 513, objectFit: 'contain' }}
            />
          </div>
        )}
      </Modal>
    </>
  )
}

export default FixSkuList
